import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { QRCodeCanvas } from "qrcode.react";
import { ButtonWithIcon } from "./ButtonWithIcon";
import { LabeledTextInput } from "./TextInput";
import { Loader } from "@mantine/core";

import {
	setupTOTP,
	verifyTotpToken,
	initMFA,
	logOut,
} from "../actions/userAuthActions";

import {
	TOTP_SETUP_CODE_GENERATED,
	VERIFY_TOTP_TOKEN_SUCCESS,
	VERIFY_TOTP_TOKEN_FAILURE,
} from "../actions/types";
import { LexWorkplaceCheckIcon } from "./LexWorkplaceIcons";

// The "you gotta setup MFA" popup dialog
const MFARequiredPrompt = ({ onGenerateQRCode, onLogout }) => {
	return (
		<div>
			<div style={{ marginBottom: "24px" }}>
				Your firm requires Multi-Factor Authentication (MFA) to access
				and use LexWorkplace. You must setup MFA for your login to
				continue using LexWorkplace.
			</div>
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}
			>
				<ButtonWithIcon
					handler={onGenerateQRCode}
					buttonText="Generate QR Code"
					iconComponent={<LexWorkplaceCheckIcon />}
					className="blue"
				/>
				<ButtonWithIcon handler={onLogout} buttonText="Logout" />
			</div>
		</div>
	);
};

// QR Code, enter 1st MFA code setup screen
const SetupMFACode = ({
	qrCodeUrl,
	code,
	onCodeChange,
	onConfirm,
	isConfirmDisabled,
}) => {
	return (
		<div>
			<div style={{ textAlign: "center", marginBottom: "20px" }}>
				Scan this QR code using your MFA client app (Google
				Authenticator) and enter the generated code below to complete
				setup.
			</div>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					marginBottom: "20px",
				}}
			>
				<div
					style={{
						border: "1px solid #ccc",
						borderRadius: "36px",
						padding: "36px",
						display: "inline-block",
					}}
				>
					<QRCodeCanvas
						value={qrCodeUrl}
						size={256}
						style={{ display: "block" }}
					/>
				</div>
			</div>

			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					width: "100%",
				}}
			>
				<div style={{ width: 240, paddingBottom: 12 }}>
					<LabeledTextInput
						label="ENTER MFA CODE"
						name="code"
						placeholder="code"
						autocomplete="one-time-code"
						value={code}
						onChange={onCodeChange}
					/>

					<div
						style={{
							display: "flex",
							justifyContent: "center",
							marginTop: "10px",
						}}
					>
						<ButtonWithIcon
							disabled={isConfirmDisabled}
							handler={onConfirm}
							buttonText="Confirm MFA"
							iconComponent={<LexWorkplaceCheckIcon />}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

class ConfigureMFA extends Component {
	constructor(props) {
		super(props);
		this.state = {
			code: "",
		};
		this.handleChange = this.handleChange.bind(this);
		this.submit = this.submit.bind(this);
		this.generateErrorMessage = this.generateErrorMessage.bind(this);
		this.generateQRCodeUrl = this.generateQRCodeUrl.bind(this);
	}

	handleChange(key, value) {
		this.setState({ [key]: value }, this.validate);
	}
	generateErrorMessage() {}
	componentDidMount() {
		this.props.initMFA();
		// For optional mode, automatically generate QR code
		if (this.props.mode === "optional") {
			this.props.setupTOTP();
		}
	}
	submit() {
		if (
			(this.props.codeGenerated || this.props.mfaConfiguredFailure) &&
			this.state.code.length > 0
		) {
			this.props.verifyTotpToken(this.state.code);
		} else {
			this.props.setupTOTP();
		}
	}
	generateQRCodeUrl(secret) {
		const issuer = "LexWorkplace";
		const str =
			"otpauth://totp/" +
			this.props.userEmail +
			"?secret=" +
			secret +
			"&issuer=" +
			issuer;
		return str;
	}
	render() {
		// Determine which component to show
		const showQRCodeSetup =
			this.props.codeGenerated || this.props.mfaConfiguredFailure;
		const showInitialPrompt =
			this.props.mode === "required" && !showQRCodeSetup;
		const showLoadingSpinner =
			this.props.mode === "optional" && !showQRCodeSetup;

		return (
			<div className="newCenter450">
				{this.props.mfaConfiguredFailure ? (
					<div className="inlineErrorIndicator">
						{this.props.errorMessage}
					</div>
				) : null}

				{showInitialPrompt ? (
					<MFARequiredPrompt
						onGenerateQRCode={this.submit}
						onLogout={() => this.props.logOut()}
					/>
				) : null}

				{showQRCodeSetup ? (
					<SetupMFACode
						qrCodeUrl={this.generateQRCodeUrl(
							this.props.mfaSetupCode
						)}
						code={this.state.code}
						onCodeChange={this.handleChange}
						onConfirm={this.submit}
						isConfirmDisabled={this.state.code.length === 0}
					/>
				) : null}

				{showLoadingSpinner ? (
					<div style={{ textAlign: "center", padding: "20px" }}>
						<Loader />
					</div>
				) : null}
			</div>
		);
	}
}
ConfigureMFA.propTypes = {
	errorMessage: PropTypes.string.isRequired,
	infoMessage: PropTypes.string,
	username: PropTypes.string.isRequired,
	userEmail: PropTypes.string.isRequired,
	mfaSetupCode: PropTypes.string.isRequired,
	setupTOTP: PropTypes.func.isRequired,
	verifyTotpToken: PropTypes.func.isRequired,
	initMFA: PropTypes.func.isRequired,
	mode: PropTypes.oneOf(["required", "optional"]),
};

ConfigureMFA.defaultProps = {
	mode: "optional",
};
const mapStateToProps = (state) => ({
	errorMessage: state.user.errorMessage,
	infoMessage: state.user.infoMessage,
	username: state.user.username,
	userEmail: state.user.userEmail,
	mfaSetupCode: state.user.mfaSetupCode,
	codeGenerated: state.user.authState === TOTP_SETUP_CODE_GENERATED,
	mfaConfiguredSuccess: state.user.authState === VERIFY_TOTP_TOKEN_SUCCESS,
	mfaConfiguredFailure: state.user.authState === VERIFY_TOTP_TOKEN_FAILURE,
});
export default connect(mapStateToProps, {
	setupTOTP,
	verifyTotpToken,
	initMFA,
	logOut,
})(ConfigureMFA);
